/* The campaign filter vocabulary, and which of it can be previewed honestly.
 *
 * A campaign's filters are the object candidacy.py applies, and the engine
 * knows thirty-one names. All thirty-one are offered here, because James's
 * call on 2026-09-15 was the jet cockpit: a person building a campaign knows
 * what they are doing and wants every setting, with the common ones first and
 * the rest folded away rather than removed.
 *
 * Only some of those names mean exactly the same thing as a column on
 * api.property, and only those can be counted live. The rest are shown as
 * applied-on-the-run rather than previewed, because a preview that disagrees
 * with the result is worse than no preview: somebody tunes against a number,
 * runs it, and gets a different book.
 *
 * Each mapping below was read against the predicate in candidacy.py rather than
 * assumed, and the ones that did not match exactly are deliberately absent:
 *
 *   has_owner   the engine picks one owner per property across both the
 *               property and project stakeholder paths, preferring a party that
 *               builds over one that advises. api.property.owner_company_id is
 *               only a property-path role of owner, so the two disagree.
 *   has_reachable_person, owner_party_not_in, standing_stock, and the rest
 *               need joins the api schema does not expose, and measuring them
 *               live took seconds rather than milliseconds.
 *
 * scripts/check_campaign_criteria.py holds this list against
 * api.criteria_filter_names(), so a criterion the engine gains or loses breaks
 * the build here rather than being silently missing from the only screen that
 * can set it.
 */

export type FilterValue = string[] | number | boolean | string;
export type Filters = Record<string, FilterValue>;

/* The six things a campaign is actually about. A flat list of thirty-one
   controls is as unusable as the six that were here before, in the other
   direction. */
/* Plain nouns. James, 2026-09-22: "Show who owns it" is clever where the
   reader wants a label. This is a jet cockpit, so the group says what is
   inside it and nothing else. */
export const GROUPS = [
  "Location",
  "Property",
  "Project",
  "Ownership",
  "Contacts",
  "Incentives and ordinances",
  "Where the lead came from",
] as const;

export type GroupName = (typeof GROUPS)[number];

/* stage_words: the project statuses, chosen by Buzze's stage words from
   api.stage_word (components/StageWords.tsx). The value is still a list of
   statuses, so candidacy reads what it always did.

   year_of and unknowns are settings the builder's own Year and Size sections
   and each range's box set (engine migration 275). They select nothing by
   themselves, so the groups below leave them out. */
export type CriterionType = "text_list" | "enum_list" | "number" | "boolean" | "chargers" | "stage_words"
  | "year_of" | "unknowns";

export type Criterion = {
  /** The name the engine knows. */
  key: string;
  label: string;
  group: GroupName;
  type: CriterionType;
  /** Every value the column holds, for an enum_list. */
  options?: readonly string[];
  /** One line, and only where the criterion would otherwise mislead. */
  help?: string;
  /** Shown before anybody opens a group. These six were the whole builder
   *  until 2026-09-15, so a person who knew it finds nothing moved. */
  core?: true;
};

/* Value sets read off the live lake on 2026-09-15 rather than invented. A
   value the lake grows later still saves, because the engine validates the
   filter name and not its contents, but it will not appear as a choice until
   it is added here. */
const PROJECT_KIND = ["new_construction", "retrofit", "expansion"] as const;
// unknown: no source has stated a type (engine migration 260). A campaign that
// wants those properties ticks it, and one that does not leaves them out.
const PROPERTY_TYPE = ["multifamily", "commercial", "mixed_use", "other", "unknown"] as const;
const AFFORDABLE_STATE = ["yes", "no", "unknown"] as const;
const STAKEHOLDER_ROLE = ["owner", "developer", "property_manager", "asset_manager",
                          "general_contractor", "architect"] as const;
const OWNER_PARTY = ["public_agency", "consultant"] as const;

/* `core` is the set shown before anything is expanded.
 *
 * Chosen from what the three real California campaigns actually set, read off
 * api.criteria_set on 2026-09-22, rather than from what looked important:
 *
 *   state_in              3 of 3
 *   has_owner             2 of 3
 *   owner_party_not_in    2 of 3
 *   min_units             1 of 3
 *   has_connected_person  1 of 3
 *   has_reachable_person  1 of 3
 *   standing_stock        1 of 3
 *   property_type_in      1 of 3
 *   chargers_in           1 of 3
 *   city_in               0 of 3
 *   max_units             0 of 3
 *
 * So city and the upper unit bound came out of core and four that get used
 * every time went in. Nothing was removed from the screen, only from the
 * front of it.
 */
export const CRITERIA: readonly Criterion[] = [
  // ---- Where ----------------------------------------------------------
  { key: "state_in", label: "State", group: "Location", type: "text_list",
    help: "Two-letter codes, comma separated.", core: true },
  { key: "state_not_in", label: "Exclude states", group: "Location", type: "text_list",
    help: "Two-letter codes, comma separated." },
  { key: "city_in", label: "City", group: "Location", type: "text_list",
    help: "Comma separated." },
  { key: "has_coordinates", label: "Coordinates known", group: "Location", type: "boolean",
    help: "The property is placed on a map." },

  // ---- The property ---------------------------------------------------
  { key: "property_type_in", label: "Property type", group: "Property",
    type: "enum_list", options: PROPERTY_TYPE, core: true },
  /* A range leaves out the properties with no value unless its box under the
     range keeps them (include_unknown, engine migration 275). The box says so
     with the count, so the help line that said "Excludes properties with no
     unit count." is gone. */
  { key: "min_units", label: "Min units", group: "Property", type: "number", core: true },
  { key: "max_units", label: "Max units", group: "Property", type: "number" },
  { key: "include_unknown", label: "Properties with no value", group: "Property", type: "unknowns" },
  { key: "has_units", label: "Unit count known", group: "Property",
    type: "boolean", help: "Zero counts as unknown." },
  { key: "chargers_in", label: "Existing chargers", group: "Property", type: "chargers", core: true },
  { key: "standing_stock", label: "Already built", group: "Property", type: "boolean", core: true },
  /* Three states, because 15,186 of 18,687 properties hold no answer and that
     is different from not being affordable. Nestor, 2026-08-27: affordable
     means 66 percent or more of units income restricted, which is the trigger
     every incentive program uses, and LIHTC lists are a segment they run
     campaigns on. */
  { key: "affordable_in", label: "Affordable housing", group: "Property",
    type: "enum_list", options: AFFORDABLE_STATE },
  // CoStar's property facts, stored since 2026-09-28 and filters since
  // engine migration 158. Year built is one of the Year section's years since
  // engine migration 275.
  { key: "building_class_in", label: "Property class", group: "Property", type: "text_list",
    help: "CoStar's class, such as A, B or C. Comma separated." },
  { key: "stories_min", label: "Min stories", group: "Property", type: "number" },

  // ---- The project ----------------------------------------------------
  { key: "project_status_in", label: "Project stage", group: "Project",
    type: "stage_words" },
  { key: "project_kind_in", label: "Project type", group: "Project",
    type: "enum_list", options: PROJECT_KIND },
  /* One year range on the year Year of names, replacing Built in or after,
     Min and Max permit year and Permit year known (James, 2026-10-20, engine
     migration 275). */
  { key: "year_of", label: "Year of", group: "Project", type: "year_of" },
  { key: "year_min", label: "From year", group: "Project", type: "number" },
  { key: "year_max", label: "To year", group: "Project", type: "number" },

  // ---- Who owns it ----------------------------------------------------
  { key: "has_owner", label: "Owner known", group: "Ownership", type: "boolean", core: true },
  { key: "min_portfolio", label: "Min properties owned",
    group: "Ownership", type: "number", help: "Counted from our data." },
  { key: "stakeholder_role_in", label: "Company role", group: "Ownership", type: "enum_list",
    options: STAKEHOLDER_ROLE },
  { key: "owner_party_not_in", label: "Exclude owner types", group: "Ownership", type: "enum_list",
    options: OWNER_PARTY, core: true },

  // ---- Who we can reach -----------------------------------------------
  { key: "has_connected_person", label: "Someone connected", group: "Contacts", type: "boolean",
    help: "A person is connected to the property.", core: true },
  { key: "has_reachable_person", label: "Someone to call is reachable", group: "Contacts", type: "boolean",
    help: "Skips properties enrichment could fix. For a list called today." },
  { key: "has_email", label: "Email", group: "Contacts", type: "boolean" },
  { key: "has_mobile", label: "Mobile", group: "Contacts", type: "boolean" },
  { key: "has_linkedin", label: "LinkedIn", group: "Contacts", type: "boolean" },

  // ---- Local rules and programs ---------------------------------------
  { key: "has_ordinance", label: "Any ordinance", group: "Incentives and ordinances", type: "boolean" },
  { key: "has_local_ordinance", label: "Local ordinance", group: "Incentives and ordinances", type: "boolean",
    help: "City or county." },
  { key: "requires_evse", label: "Chargers required", group: "Incentives and ordinances", type: "boolean",
    help: "Not just conduit." },
  { key: "has_right_to_charge", label: "Right to charge", group: "Incentives and ordinances", type: "boolean" },
  { key: "preempted", label: "Preempted by state", group: "Incentives and ordinances", type: "boolean" },
  { key: "has_program", label: "Incentive program", group: "Incentives and ordinances", type: "boolean" },
  { key: "min_rate_pct", label: "Min incentive rate", group: "Incentives and ordinances",
    type: "number", help: "Percent." },

  /* Nestor asked for this on 2026-08-27: he splits a LinkedIn-sourced list and
     a publication-sourced list into different campaigns, and wanted to filter
     the data based off of those sources.

     Matched as text rather than chosen from a list, because a source carries
     its own date. "Yardi Matrix (2026-07-31)" and "Yardi Matrix (2026-03-01)"
     are one source to a rep, so typing Yardi has to find both, and the long
     tail of listing sites and publications would make a fixed list stale in a
     week. candidacy.source_matches runs the same substring test. */
  { key: "source_in", label: "Source", group: "Where the lead came from",
    type: "text_list",
    help: "Part of a source name, comma separated. Yardi, ChargePoint, linkedin." },
  { key: "source_not_in", label: "Exclude sources", group: "Where the lead came from",
    type: "text_list",
    help: "Part of a source name, comma separated." },
];

/* The years a year range can read, each a column on api.candidate_property
   (engine migration 275), in the order the Year section offers them. James,
   2026-10-20: these four. The permit year is the engine's estimate, which is
   the trap its help line names. */
export const YEAR_OF = [
  { value: "expected_completion", label: "Expected completion", column: "year_expected_completion",
    none: "no expected completion" },
  { value: "construction_start", label: "Construction start", column: "year_construction_start",
    none: "no construction start" },
  { value: "permit", label: "Permit (estimated)", column: "year_permit_estimate",
    none: "no permit estimate", help: "18 months before completion, or the construction start." },
  { value: "built", label: "Year built", column: "year_built", none: "no year built" },
] as const;

export type YearChoice = (typeof YEAR_OF)[number];

/** The year a campaign's range reads, Expected completion when none is set. */
export function yearOf(value: unknown): YearChoice {
  return YEAR_OF.find((y) => y.value === value) ?? YEAR_OF[0];
}

/* The short line James asked for under the year, 2026-10-20. */
export const YEAR_NOTE = "Dates come from many sources, so results are approximate.";

/* What a range measures: the keys that set its bounds, and the words for a
   property with no value. include_unknown names these. */
export const MEASURES = {
  units: { keys: ["min_units", "max_units"], none: "no unit count" },
  stories: { keys: ["stories_min"], none: "no stories on record" },
  year: { keys: ["year_min", "year_max"], none: "no year" },
  // Since engine migration 285 (James, 2026-10-23).
  portfolio: { keys: ["min_portfolio"], none: "no owner on record" },
  rate: { keys: ["min_rate_pct"], none: "no incentive on record" },
} as const;

export type Measure = keyof typeof MEASURES;

/** The column a measure reads on api.candidate_property. */
export function measureColumn(m: Measure, filters: Filters): string {
  if (m === "units") return "unit_count";
  if (m === "stories") return "stories";
  if (m === "portfolio") return "portfolio_size";
  if (m === "rate") return "best_rate_pct";
  return yearOf(filters.year_of).column;
}

/* What no value is in a measure's column, as a PostgREST test. An owner EVIE
   does not know is a portfolio_size of 0, never NULL (23,366 of 69,401
   candidates on 2026-10-23). Every other measure's missing value is NULL.
   candidacy.MEASURES reads the same. */
function noValueTest(m: Measure, col: string): string {
  return m === "portfolio" ? `${col}.eq.0` : `${col}.is.null`;
}

/** The query narrowed to the properties with no value for this measure. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withNoValue(query: any, m: Measure, filters: Filters): any {
  const col = measureColumn(m, filters);
  return m === "portfolio" ? query.eq(col, 0) : query.is(col, null);
}

/** "no unit count", or for the year, the year the range reads. */
export function noneWords(m: Measure, filters: Filters): string {
  return m === "year" ? yearOf(filters.year_of).none : MEASURES[m].none;
}

/** Whether any bound of this measure is set. */
export function rangeSet(filters: Filters, m: Measure): boolean {
  return MEASURES[m].keys.some((k) => isSet(filters[k]));
}

/** Whether the campaign keeps this measure's properties with no value. */
export function keepsUnknown(filters: Filters, m: Measure): boolean {
  const kept = filters.include_unknown;
  return Array.isArray(kept) && kept.includes(m);
}

/** The filters with this measure kept or left out. An empty list is no key. */
export function withKeep(filters: Filters, m: Measure, keep: boolean): Filters {
  const now = Array.isArray(filters.include_unknown) ? (filters.include_unknown as string[]) : [];
  const list = keep ? Array.from(new Set([...now, m])) : now.filter((x) => x !== m);
  const next = { ...filters };
  if (list.length) next.include_unknown = list;
  else delete next.include_unknown;
  return next;
}

/** The filters with one bound changed. Setting a measure's first bound keeps
 *  the properties with no value, and the box under it leaves them out (James,
 *  2026-10-23, for every range; for the year since 2026-10-20, because two
 *  thirds of New Construction has no date). Clearing a measure's last bound
 *  drops its keep, and the year's last bound drops Year of, so a saved
 *  campaign holds no setting that does nothing. */
export function withBound(filters: Filters, key: string, value: number | undefined): Filters {
  const m = (Object.keys(MEASURES) as Measure[]).find((x) => (MEASURES[x].keys as readonly string[]).includes(key));
  const before = m ? rangeSet(filters, m) : false;
  let next: Filters = { ...filters };
  if (value === undefined) delete next[key];
  else next[key] = value;
  if (!m) return next;
  const after = rangeSet(next, m);
  if (m === "year" && after && !isSet(next.year_of)) next.year_of = YEAR_OF[0].value;
  if (!before && after) next = withKeep(next, m, true);
  if (!after) {
    next = withKeep(next, m, false);
    if (m === "year") delete next.year_of;
  }
  return next;
}

/* A list whose column can hold no value: property type, existing chargers
   and affordable housing. The value for "not known" is not a choice in the
   list. It is a box under it, shown once a known value is ticked, with how
   many of the properties the other criteria match it would add, the same
   box a range has. James, 2026-10-22: let people decide whether records
   with no data are in, rather than the filter leaving them out unseen.
   Nestor's New Mexico campaign ticked Multifamily, and the 34 properties his
   file made with no type were off the list with nothing on screen to say so.
   The stored value is unchanged ("unknown" in the list), so the engine and
   every saved campaign read as before. Since 2026-10-23 the box starts
   ticked (withChoice), and property class has one too (engine migration
   285): 69,308 of 69,401 candidates have no class. */
export const LIST_UNKNOWN: Record<string, { value: string; column: string; isNull?: true; none: string }> = {
  property_type_in: { value: "unknown", column: "property_type", none: "no type on record" },
  chargers_in: { value: "unknown", column: "chargers_state", none: "charger status not known" },
  affordable_in: { value: "unknown", column: "affordable_flag", isNull: true, none: "affordability not known" },
  building_class_in: { value: "unknown", column: "building_class", isNull: true, none: "no class on record" },
};

/** The known values a list offers, without its "not known" value. */
export function knownChoices(key: string, options: readonly string[]): string[] {
  const u = LIST_UNKNOWN[key];
  return u ? options.filter((o) => o !== u.value) : [...options];
}

/** Whether a list keeps the properties with no value. */
export function listKeepsUnknown(filters: Filters, key: string): boolean {
  const u = LIST_UNKNOWN[key];
  const v = filters[key];
  return Boolean(u && Array.isArray(v) && (v as string[]).includes(u.value));
}

/** Whether any known value of a list is ticked, which is when its box shows. */
export function listHasKnown(filters: Filters, key: string): boolean {
  const u = LIST_UNKNOWN[key];
  const v = filters[key];
  return Array.isArray(v) && (v as string[]).some((x) => !u || x !== u.value);
}

/** The filters with one known value of a list ticked or unticked. Ticking the
 *  first known value ticks the box too, so the properties with no value stay
 *  on the list until someone unticks it (James, 2026-10-23). Unticking the
 *  last known value takes the filter off, box and all, so no campaign is left
 *  asking for the properties with no value alone without having chosen that. */
export function withChoice(filters: Filters, key: string, value: string, on: boolean): Filters {
  const cur = Array.isArray(filters[key]) ? (filters[key] as string[]) : [];
  const u = LIST_UNKNOWN[key];
  const first = on && u !== undefined && !listHasKnown(filters, key);
  const list = on ? Array.from(new Set([...cur, value, ...(first ? [u.value] : [])]))
    : cur.filter((x) => x !== value);
  const next = { ...filters };
  if (!list.some((x) => !u || x !== u.value)) delete next[key];
  else next[key] = list;
  return next;
}

/** The filters with a typed list changed, for a typed list whose value can be
 *  not known (property class). The text holds the known values only, and the
 *  box keeps its state, ticked when the first value is typed. */
export function withTypedList(filters: Filters, key: string, values: string[]): Filters {
  const u = LIST_UNKNOWN[key];
  const next = { ...filters };
  const known = values.filter((x) => !u || x.toLowerCase() !== u.value);
  if (!known.length) {
    delete next[key];
    return next;
  }
  const keep = u !== undefined && (listHasKnown(filters, key) ? listKeepsUnknown(filters, key) : true);
  next[key] = keep && u ? [...known, u.value] : known;
  return next;
}

/** The filters with a list's box ticked or unticked. */
export function withListKeep(filters: Filters, key: string, keep: boolean): Filters {
  const u = LIST_UNKNOWN[key];
  if (!u || !listHasKnown(filters, key)) return filters;
  const cur = filters[key] as string[];
  const next = { ...filters };
  next[key] = keep ? Array.from(new Set([...cur, u.value])) : cur.filter((x) => x !== u.value);
  return next;
}

/** The filters as saved: no Year of without a year range and no keep for a
 *  measure with no bound, which would be settings that do nothing. */
export function forSaving(filters: Filters): Filters {
  let next = { ...filters };
  for (const m of Object.keys(MEASURES) as Measure[]) {
    if (!rangeSet(next, m)) next = withKeep(next, m, false);
  }
  if (!rangeSet(next, "year")) delete next.year_of;
  return next;
}

/* A stored value read as English, for the criteria that carry a fixed set of
   them. The value itself is what the engine receives and does not change.

   Only for enum_list and chargers. A state code must stay CA rather than
   becoming Ca, and a source is text somebody typed. */
export function optionLabel(value: string): string {
  const words = String(value).replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function readsAsOptions(key: string): boolean {
  const type = criterion(key)?.type;
  return type === "enum_list" || type === "chargers";
}

export function criterion(key: string): Criterion | undefined {
  return CRITERIA.find((c) => c.key === key);
}

export function criteriaIn(group: GroupName): Criterion[] {
  return CRITERIA.filter((c) => c.group === group);
}

/* ---------------------------------------------------------------------------
 * Nestor's segments, as presets rather than as a stored field.
 *
 * His Campaign OS intake form offers four. Three of them decompose exactly
 * into criteria the engine already applies. The fourth does not, and saying so
 * on screen is the whole point of doing it this way: building Segment as a
 * field would mean inventing a distinction the lake cannot hold.
 * ------------------------------------------------------------------------ */

export type Weights = Record<string, number>;

/** A ranking a campaign can be saved with. "engine" is not one a rep picks:
    it is a campaign whose weights were set in the engine and match neither
    profile, and saving it leaves those weights alone. */
export type RankKey = "contacts" | "property" | "none";
export type RankState = RankKey | "engine";

export type Preset = {
  label: string;
  /** What it sets. Replaces what another segment set. */
  filters: Filters;
  /** The ranking choosing this segment sets. The rep can change it after. */
  rankBy: RankKey;
  /** Said on screen after it is applied. */
  note?: string;
};

/* The two ranking profiles.
 *
 * A campaign's weights say what makes one property better than another. The
 * engine scores 23 factors and bands the result into P1, P2 and P3.
 *
 * WHERE THESE CAME FROM, AND WHAT CHANGED
 *
 * They started as `california-best-leads` and `california-chargepoint-
 * replacement` copied exactly off the live lake, which answered James's
 * question about whether the old weights were over-fitted to California. They
 * were not: `california-best-leads` and `colorado-best-leads` carry the same
 * profile renormalized, so they were tuned to what the campaign was for rather
 * than to where it was.
 *
 * They are no longer copies. Three measured changes on 2026-09-22, each one
 * with the number that forced it:
 *
 * 1. ordinance_evse fell from 10 to 2 in the property-led profile, and from
 *    4.15 to 1 in the contact-led one. It is true for 3,900 of the 3,958
 *    properties in the retrofit segment, which is 98.5 percent. A factor true
 *    for almost every row gives almost every row the same points and sorts
 *    nobody, while eating a tenth of the scale. Whether a property sits under
 *    an EVSE mandate decides whether it is a lead at all, which makes it a
 *    filter rather than a weight. A token 1 to 2 stays so a market with no
 *    mandates still reads differently.
 *
 * 2. geocoded, any_contacts and linkedins were turned on. All three already
 *    existed in the engine and neither profile used them. geocoded is the best
 *    discriminator available on this book: 7,356 of 18,742 properties are
 *    rooftop precision, which is 39.2 percent, and a factor near half coverage
 *    sorts far more rows than one near 98.
 *
 * 3. project_contacts is new, added by engine migration 103. A person named on
 *    this property's own project rather than a title at the parent company.
 *
 *    It is weighted 11 for New Construction and 1 for the retrofits, and that
 *    gap is the whole lesson of the first attempt. It was 8 in both, measured
 *    afterwards: 747 of 3,164 new-construction candidates have one, which is
 *    23.6 percent, against 83 of 15,578 standing properties, which is 0.5
 *    percent. lake.project_person is filled by entitlement filings, and a
 *    filing is made before a property exists, so a finished property almost
 *    never has one.
 *
 *    Eight points on a factor present in 0.5 percent of rows wastes the scale
 *    exactly as ten points on one present in 98.5 percent does. **The coverage
 *    test cuts both ways.** Too rare and too common both sort nobody, and the
 *    first version of these profiles caught one end and missed the other.
 *
 * WHAT DECIDED THE SIZES
 *
 * A factor's power to rank comes from how much it varies, not from how much
 * the fact matters commercially. Two candidates were measured and rejected on
 * that test. Website coverage runs 45 percent at one property, 69 at 2 to 5,
 * 88 at 6 to 20 and 98 at 20+, so it is mostly a restatement of portfolio size
 * and would count one signal twice. And 86.9 percent of properties have
 * exactly one source, so a source count sorts almost nobody.
 *
 * Tiebreakers are small on purpose. They are meant to separate a 65 from a 62
 * from a 58, not to decide the order.
 *
 * Both sum to 100, which api.save_criteria_set and candidacy.check_weights
 * both enforce, and scripts/test_presets.mjs asserts. The numbers are round
 * now because they are authored rather than renormalized, so they can be
 * reasoned about and adjusted.
 *
 * A third profile exists on the lake, portfolio-first, where spread and depth
 * carry two thirds. No segment uses it. It is the first candidate if a fourth
 * segment is ever wanted.
 */
const CONTACT_LED: Weights = {
  mobiles: 15,
  buying_contacts: 14,
  stage_heat: 11,
  units: 11,
  project_contacts: 11,
  recency: 9,
  portfolio_depth: 6,
  geocoded: 5,
  portfolio_spread: 4,
  senior_contacts: 4,
  any_contacts: 3,
  emails: 3,
  owner_in_hubspot: 2,
  ordinance_evse: 1,
  linkedins: 1,
};

const PROPERTY_LED: Weights = {
  units: 32,
  portfolio_depth: 19,
  portfolio_spread: 11,
  affordable: 10,
  program_coverage: 8,
  geocoded: 7,
  owner_in_hubspot: 5,
  any_contacts: 4,
  ordinance_evse: 2,
  linkedins: 1,
  project_contacts: 1,
};

/* The rankings a campaign can be saved with, shown on the builder as Rank by.
 *
 * Until 2026-09-24 the ranking was read back off the filters: a campaign was
 * ranked only while its filters matched a segment exactly. NJ Test pressed New
 * Construction, then added "completed" to Project status, which turned the
 * segment off and saved the campaign unranked, with one line of hint text as
 * the only sign. And a campaign whose weights were set in the engine, such as
 * California, matched no segment, so saving it here sent {} and would have
 * unranked it.
 *
 * So the ranking is its own choice on screen. A segment sets it, a rep can
 * change it, and changing a filter leaves it alone. */
export const RANKINGS: readonly { key: RankKey; label: string; says: string; weights: Weights }[] = [
  { key: "contacts", label: "Who can be reached",
    says: "Mobiles, buying contacts, project stage and size count most.",
    weights: CONTACT_LED },
  { key: "property", label: "The property",
    says: "Unit count and the owner's portfolio count most.",
    weights: PROPERTY_LED },
  { key: "none", label: "Not ranked",
    says: "No score. Listed by unit count.",
    weights: {} },
];

export function rankingOf(key: RankKey) {
  return RANKINGS.find((r) => r.key === key)!;
}

/* The ranking a saved campaign has. Weights equal to a profile are that
   profile, none are Not ranked, and anything else was set in the engine. */
export function rankingFor(weights: Weights | null | undefined): RankState {
  const w = weights ?? {};
  const keys = Object.keys(w);
  if (!keys.length) return "none";
  for (const r of RANKINGS) {
    const rk = Object.keys(r.weights);
    if (rk.length === keys.length && rk.every((k) => w[k] === r.weights[k])) return r.key;
  }
  return "engine";
}

/* What choosing a segment does, in one sentence, so it is not hidden. */
export function presetSays(preset: Preset): string {
  const fields = Object.keys(preset.filters)
    .map((k) => CRITERIA.find((c) => c.key === k)?.label ?? k);
  const last = fields.pop();
  const listed = fields.length ? `${fields.join(", ")} and ${last}` : last;
  return `Sets ${listed}, and ranks by ${rankingOf(preset.rankBy).label.toLowerCase()}.`;
}

export const PRESETS: readonly Preset[] = [
  {
    label: "New Construction",
    filters: {
      // Every stage before Opening / Delivered, so each of Buzze's words
      // reads as fully on in the Project stage control, and Stage not known,
      // which those projects read as Planning / Proposed until engine
      // migration 270 (2026-10-20).
      project_status_in: ["prospective", "rumored", "site_acquired", "design_review", "permitting",
                          "permitted", "funded", "groundbreaking", "under_construction", "unknown"],
      standing_stock: false,
    },
    rankBy: "contacts",
  },
  {
    label: "Retrofit: Swap Outs",
    filters: { standing_stock: true, chargers_in: ["present"] },
    rankBy: "property",
  },
  {
    label: "Retrofit: Full Scope",
    filters: { standing_stock: true, chargers_in: ["absent", "unknown"] },
    rankBy: "property",
  },
];

/* Retrofit: EV-Ready to Install was the fourth and is removed, 2026-09-22.
 *
 * It set standing_stock true and chargers_in absent, and carried a note saying
 * conduit is not in our data so it matches Full Scope. That was honest and it
 * still offered a rep a choice with nothing behind it: two buttons returning
 * the same properties under different names, where the difference a rep would
 * read into the label is the one thing the lake cannot tell them.
 *
 * It comes back when the lake holds whether a property is wired for chargers
 * without having them. Until then the segment is Full Scope.
 */

/* ---------------------------------------------------------------------------
 * The preview.
 * ------------------------------------------------------------------------ */

/* Named, with how the preview counts it. Twenty-seven of the thirty-one.
 *
 * Every one of these is a column on api.candidate_property, which migration
 * 079 built from candidacy's own query with the Python predicates moved into
 * it. So a criterion is not translated here, it is named: chargers_in reads
 * chargers_state because chargers_state is what candidacy now reads too.
 *
 * Before 079 this list was nine, each one hand-matched against a predicate in
 * candidacy.py, and the four that looked close enough were deliberately left
 * out. That is the shape of thing that drifts, and it did: the preview said
 * 102 and the run froze 53.
 */
export const PREVIEWABLE = {
  // ---- Where ----------------------------------------------------------
  state_in: { kind: "list" as const, column: "state" },
  state_not_in: { kind: "not_list" as const, column: "state" },
  city_in: { kind: "list" as const, column: "city" },
  has_coordinates: { kind: "bool" as const, column: "has_coordinates" },

  // ---- The property ---------------------------------------------------
  property_type_in: { kind: "list" as const, column: "property_type" },
  min_units: { kind: "min" as const, column: "unit_count", measure: "units" as const },
  max_units: { kind: "max" as const, column: "unit_count", measure: "units" as const },
  building_class_in: { kind: "list" as const, column: "building_class" },
  stories_min: { kind: "min" as const, column: "stories", measure: "stories" as const },
  has_units: { kind: "bool" as const, column: "has_units" },
  chargers_in: { kind: "list" as const, column: "chargers_state" },
  affordable_in: { kind: "tristate" as const, column: "affordable_flag" },
  source_in: { kind: "contains_any" as const, column: "data_source" },
  source_not_in: { kind: "contains_none" as const, column: "data_source" },
  standing_stock: { kind: "bool" as const, column: "standing_stock" },

  // ---- The project ----------------------------------------------------
  project_status_in: { kind: "list" as const, column: "project_status" },
  project_kind_in: { kind: "list" as const, column: "project_kind" },
  // The column is the one Year of names (yearOf), read when the count is made.
  year_min: { kind: "min" as const, column: "", measure: "year" as const },
  year_max: { kind: "max" as const, column: "", measure: "year" as const },

  // ---- Who owns it ----------------------------------------------------
  has_owner: { kind: "bool" as const, column: "has_owner" },
  min_portfolio: { kind: "min" as const, column: "portfolio_size", measure: "portfolio" as const },
  stakeholder_role_in: { kind: "list" as const, column: "stakeholder_role" },

  // ---- Who we can reach -----------------------------------------------
  has_connected_person: { kind: "bool" as const, column: "has_connected_person" },
  has_reachable_person: { kind: "bool" as const, column: "has_reachable_person" },
  has_email: { kind: "bool" as const, column: "has_email" },
  has_mobile: { kind: "bool" as const, column: "has_mobile" },
  has_linkedin: { kind: "bool" as const, column: "has_linkedin" },

  // ---- Local rules and programs ---------------------------------------
  has_ordinance: { kind: "bool" as const, column: "has_ordinance" },
  has_local_ordinance: { kind: "bool" as const, column: "has_local_ordinance" },
  // The only nullable boolean of the set. candidacy reads it as bool(value),
  // so a null is false there, and PostgREST would drop a null from both arms
  // of an is.true / is.false test. Hence its own kind.
  requires_evse: { kind: "truthy" as const, column: "requires_evse" },
  has_right_to_charge: { kind: "bool" as const, column: "right_to_charge" },
  preempted: { kind: "bool" as const, column: "preempted" },
  has_program: { kind: "bool" as const, column: "has_program" },
  min_rate_pct: { kind: "min" as const, column: "best_rate_pct", measure: "rate" as const },
} as const;

export type PreviewableKey = keyof typeof PREVIEWABLE;

export function isPreviewable(key: string): key is PreviewableKey {
  return key in PREVIEWABLE;
}

/* The one the preview still cannot count, and what it needs.
 *
 * It needs a value the candidate row does not carry rather than arithmetic
 * over one that it does, so porting it would mean a second copy of a word
 * list. That is the thing migration 079 exists to stop. Shown on screen by key
 * so nobody has to guess which of their settings is not in the number. The
 * permit year was counted here too until engine migration 275 made its
 * estimate a column. */
export const NOT_COUNTED: Record<string, string> = {
  owner_party_not_in: "Read from the owner's name and its place when the campaign runs.",
};

/* Keys that set how a range reads and select nothing by themselves. The
   count reads them with the range, and neither is a criterion of its own. */
export const SETTINGS: readonly string[] = ["year_of", "include_unknown"];

/** The filters on a campaign that this screen cannot count. */
export function notPreviewable(filters: Filters): string[] {
  return Object.keys(filters).filter((k) => !isPreviewable(k) && !SETTINGS.includes(k) && isSet(filters[k]));
}

/** A value that actually narrows anything. An empty list does not. */
export function isSet(v: FilterValue | undefined): boolean {
  if (v === undefined || v === null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string") return v.trim() !== "";
  return true;
}

/** How many of a group's criteria are set, for the header. */
/* What is set inside the expander, which is what the chip on its header is
   promising. Core criteria are shown above the groups and are deliberately
   left out of them, so counting them here made the header say "1 set" over a
   panel that opens on nothing set. Found on screen 2026-09-22: State is core
   and also belongs to Location. */
/* Is this segment the one the criteria currently describe?
 *
 * A preset writes two or three filters and nothing recorded that it had been
 * pressed, so the four buttons looked identical whether a segment was chosen
 * or not. Rather than store which was clicked, which would go stale the moment
 * somebody changed a field underneath it, this reads the filters back: a
 * segment is on when every value it sets is currently set to that value.
 *
 * So changing Existing chargers after pressing Retrofit: Swap Outs turns the
 * button off, which is the truth.
 */
/* Every key any segment owns. A segment is a choice between four, so applying
   one has to clear what another set or the two stack up. */
export const PRESET_KEYS: readonly string[] =
  Array.from(new Set(PRESETS.flatMap((p) => Object.keys(p.filters))));

/* The filters after choosing this segment, or after unchoosing it.
 *
 * This merged over whatever was there until 2026-09-22, which stacked one
 * segment on the next. New Construction sets project_status_in to the five
 * live statuses and standing_stock false. Retrofit: Swap Outs sets
 * standing_stock true and chargers_in present, and left the project statuses
 * behind, so a finished property was also required to be under construction.
 *
 * Measured on the live lake: New Construction alone returns 3,134 properties
 * and Swap Outs alone 3,958. Clicking New Construction and then Swap Outs
 * returned 0, with nothing on the screen saying why.
 *
 * Retrofit deliberately sets no project status. standing_stock already carries
 * the project side, and project_status_in would drop every property with no
 * project row at all, which is most finished properties.
 *
 * Passing a segment that is already on turns it off, which is what a pressed
 * button that can be pressed again means.
 */
export function withPreset(filters: Filters, preset: Preset): Filters {
  const next: Filters = { ...filters };
  for (const key of PRESET_KEYS) delete next[key];
  if (presetIsOn(filters, preset)) return next;
  return { ...next, ...preset.filters };
}

export function presetIsOn(filters: Filters, preset: Preset): boolean {
  return Object.entries(preset.filters).every(([key, want]) => {
    const have = filters[key];
    if (Array.isArray(want) && Array.isArray(have)) {
      return want.length === have.length && want.every((v) => have.includes(v as never));
    }
    return have === want;
  });
}

export function setCount(filters: Filters, group: GroupName): number {
  return criteriaIn(group)
    .filter((c) => !c.core && isSet(filters[c.key])).length;
}

/* Applying the filters to a PostgREST query over api.candidate_property.
 *
 * Kept beside the definitions so the thing that counts and the thing that
 * saves read the same object. Two places deciding what state_in means is how a
 * preview and a run come to disagree. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyPreview(query: any, filters: Filters): any {
  let q = query;
  for (const [key, value] of Object.entries(filters)) {
    if (!isPreviewable(key) || !isSet(value)) continue;
    const def = PREVIEWABLE[key];
    // A range reads its measure's column, which for the year is the one
    // Year of names, and keeps the rows with no value when the campaign says
    // so (include_unknown, engine migration 275). candidacy.Range is the same
    // rule, and test_filter_preview_parity_db.py holds the two together.
    const measure = "measure" in def ? def.measure : undefined;
    const col = measure ? measureColumn(measure, filters) : def.column;
    const keep = measure ? keepsUnknown(filters, measure) : false;
    if (def.kind === "list") {
      const list = (value as string[]).filter(Boolean);
      // Property class stores no class as NULL, where property type stores
      // "unknown", so its "unknown" is a NULL test (engine migration 285).
      const u = LIST_UNKNOWN[key];
      const known = u?.isNull ? list.filter((x) => x !== u.value) : list;
      if (known.length < list.length) {
        q = known.length ? q.or(`${col}.in.(${known.join(",")}),${col}.is.null`) : q.is(col, null);
      } else if (list.length) q = q.in(col, list);
    } else if (def.kind === "not_list") {
      const list = (value as string[]).filter(Boolean);
      if (list.length) q = q.not(col, "in", `(${list.join(",")})`);
    } else if (def.kind === "min") {
      const n = Number(value);
      if (!Number.isFinite(n)) continue;
      // Without the keep, candidacy reads a missing unit count or stories as
      // 0, so a floor of zero or less keeps the rows that have no value and
      // any real floor drops them. A missing year is not read as 0. PostgREST
      // drops null from a comparison either way, which is why the cases that
      // keep it have to say so. An unknown owner's 0 passes a floor of 0 by
      // itself, so only the keep has to name it.
      const nulls = keep || (n <= 0 && measure !== "year");
      const none = measure ? noValueTest(measure, col) : `${col}.is.null`;
      q = nulls ? q.or(`${col}.gte.${n},${none}`) : q.gte(col, n);
    } else if (def.kind === "max") {
      const n = Number(value);
      // A missing value fails any ceiling in candidacy unless the campaign
      // keeps it. PostgREST drops it from the comparison. Same rows.
      if (Number.isFinite(n)) q = keep ? q.or(`${col}.lte.${n},${col}.is.null`) : q.lte(col, n);
    } else if (def.kind === "tristate") {
      // Three states over a nullable boolean, where null means nobody looked.
      // 15,186 of 18,687 properties hold null on affordable_flag, so unknown is
      // the commonest answer and is not the same as no.
      const want = (value as string[]).filter(Boolean);
      if (!want.length) continue;
      const parts = want.map((w) =>
        w === "unknown" ? `${col}.is.null` : `${col}.is.${w === "yes"}`);
      q = q.or(parts.join(","));
    } else if (def.kind === "contains_any" || def.kind === "contains_none") {
      // data_source holds every source joined by "; ", each with its own date
      // baked in, so "Yardi" has to match both the March and the July pull.
      // candidacy.source_matches is the same substring test on the same field.
      const want = (value as string[]).map((w) => String(w).trim()).filter(Boolean);
      if (!want.length) continue;
      // A comma inside a value would end the PostgREST list early and silently
      // widen the filter, so one is refused rather than guessed at.
      const safe = want.filter((w) => !/[,()]/.test(w));
      if (!safe.length) continue;
      if (def.kind === "contains_any") {
        q = q.or(safe.map((w) => `${col}.ilike.*${w}*`).join(","));
      } else {
        // A row with no source at all is excluded by nothing, so it stays.
        // candidacy.source_matches returns False for a null data_source and
        // the caller negates it, so the engine keeps that row. Expressed as
        // not.or here, PostgREST would drop it, because NOT NULL is NULL.
        // No candidate holds a null source today and one will, and a
        // disagreement between the preview and the run is the failure this
        // whole file is arranged to prevent.
        const none = safe.map((w) => `${col}.not.ilike.*${w}*`).join(",");
        q = q.or(`${col}.is.null,and(${none})`);
      }
    } else if (def.kind === "truthy") {
      q = value === true ? q.is(col, true) : q.or(`${col}.is.false,${col}.is.null`);
    } else {
      q = q.is(col, value === true);
    }
  }
  return q;
}

/** Text in the form, back to the value the engine stores. */
export function parseList(text: string): string[] {
  return text.split(",").map((s) => s.trim()).filter(Boolean);
}
